import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { ApiService } from './api.service';
import { ReferenceCountryModel } from '../models/reference-country.model';
import { TableDataSelectorModel } from '../models/table-data-selector.model';
import { ApiShortListModel } from '../models/api-list.model';
import { ReferenceZoneModel } from '../models/reference-zone.model';
import { ReferenceZonesService } from './reference-zones.service';

@Injectable({
  providedIn: 'root',
})
export class ReferenceCountriesService {
  private countryList$ = new BehaviorSubject<ReferenceCountryModel[]>([]);
  private countryListByZone$ = new BehaviorSubject<ReferenceZoneModel[]>([]);

  constructor(
    public _apiService: ApiService,
    public referenceZonesService: ReferenceZonesService
  ) {}

  getCountryList(): Observable<ReferenceCountryModel[]> {
    return this.countryList$.pipe(
      switchMap((countryList: ReferenceCountryModel[]) =>
        countryList.length ? of(countryList) : this.fetchCountryListFromApi()
      )
    );
  }

  private fetchCountryListFromApi(): Observable<ReferenceCountryModel[]> {
    return this._apiService
      .get<ApiShortListModel<ReferenceCountryModel>>('reference-countries')
      .pipe(
        map(
          (countryList: ApiShortListModel<ReferenceCountryModel>) =>
            countryList.value
        ),
        tap(countryList => this.countryList$.next(countryList))
      );
  }

  getCountryListByZone(): Observable<ReferenceZoneModel[]> {
    return this.countryListByZone$.pipe(
      switchMap((countryListByZone: ReferenceZoneModel[]) =>
        countryListByZone.length
          ? of(countryListByZone)
          : this.fetchCountryListByZoneFromApi()
      )
    );
  }

  private fetchCountryListByZoneFromApi(): Observable<ReferenceZoneModel[]> {
    const countryList$ = this.getCountryList();
    const zoneList$ = this.referenceZonesService.getList();

    return combineLatest([countryList$, zoneList$]).pipe(
      map(
        ([referenceCountryList, referenceZoneList]: [
          ReferenceCountryModel[],
          ReferenceZoneModel[],
        ]): ReferenceZoneModel[] => {
          return referenceZoneList.map((zoneInList: ReferenceZoneModel) => {
            zoneInList.countryList = referenceCountryList.filter(
              (country: ReferenceCountryModel) =>
                Number(country.zoneId) === zoneInList.id
            );

            return zoneInList;
          });
        }
      ),
      tap(countryListByZone => this.countryListByZone$.next(countryListByZone))
    );
  }

  public getCountryByZone(
    countryList: ReferenceCountryModel[]
  ): Observable<ReferenceZoneModel[]> {
    const zoneList$ = this.referenceZonesService.getList();

    return zoneList$.pipe(
      map((referenceZoneList: ReferenceZoneModel[]): ReferenceZoneModel[] => {
        return referenceZoneList.map((zoneInList: ReferenceZoneModel) => {
          zoneInList.countryList = countryList.filter(
            (country: ReferenceCountryModel) =>
              Number(country.zoneId) === zoneInList.id
          );

          return zoneInList;
        });
      }),
      tap(countryListByZone => this.countryListByZone$.next(countryListByZone))
    );
  }

  public transformCountryListToTableDataList(
    countryList: Array<ReferenceCountryModel>
  ): Array<TableDataSelectorModel> {
    return countryList.map(
      (country: ReferenceCountryModel, index): TableDataSelectorModel =>
        this.transformCountryToTableData(index, country)
    );
  }

  private transformCountryToTableData(
    index: number,
    country: ReferenceCountryModel
  ): TableDataSelectorModel {
    return {
      position: index,
      id: country.countryCode,
      text: country.name,
    };
  }
}
